import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useBlogStore from "../../store/blogStore";
import BlogCover from "../../components/BlogCover";
import blogPlaceholder from "../../images/blog_placeholder.png";

const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };

const getOrdinalSuffix = (n) => {
	if (n > 3 && n < 21) return "th";
	switch (n % 10) {
		case 1: return "st";
		case 2: return "nd";
		case 3: return "rd";
		default: return "th";
	}
};

const formatBlogDate = (dateStr) => {
	if (!dateStr) return "";
	const date = new Date(dateStr);
	if (isNaN(date.getTime())) return "";
	const day = date.getDate();
	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	return `Posted ${day}${getOrdinalSuffix(day)} ${months[date.getMonth()]}, ${date.getFullYear()}`;
};

const extractImageFromMarkdown = (text) => {
	if (!text) return null;
	const m = text.match(/!\[([^\]]*)\]\(([^)]+)\)/);
	return m ? m[2] : null;
};

const stripMarkdown = (text) => {
	if (!text) return "";
	return text
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, "")
		.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
		.replace(/https?:\/\/[^\s]+/g, "")
		.replace(/[#*<>[\]()!\n]/g, " ")
		.replace(/\s+/g, " ")
		.trim();
};

const createSlug = (title) => {
	if (!title) return "";
	return title.toLowerCase().trim()
		.replace(/[^\w\s-]/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-")
		.replace(/^-+|-+$/g, "");
};

const estimateReadTime = (content) => {
	if (!content) return "3 Min read";
	const words = stripMarkdown(content).split(/\s+/).length;
	const mins = Math.max(1, Math.round(words / 200));
	return `${mins} Min read`;
};

const BlogCard = ({ blog }) => {
	const navigate = useNavigate();
	const coverImage =
		blog.header_image_url ||
		extractImageFromMarkdown(blog.blog_content) ||
		blogPlaceholder;
	const excerpt = stripMarkdown(blog.blog_content).slice(0, 160);

	return (
		<div
			style={{
				flex: 1,
				minWidth: 0,
				display: "flex",
				flexDirection: "column",
				gap: "12px",
				cursor: "pointer",
			}}
			onClick={() => navigate(`/blog/${blog.id}/${createSlug(blog.blog_title)}`)}
		>
			<BlogCover
				src={coverImage}
				alt={blog.blog_title}
				onError={(e) => { e.target.src = blogPlaceholder; }}
				style={{
					width: "100%",
					height: "clamp(180px, 22.9vw, 330px)",
					borderRadius: "16px",
				}}
			/>

			<p
				style={{
					fontFamily: "'Geist', sans-serif",
					fontWeight: 400,
					fontSize: "clamp(10px, 0.83vw, 12px)",
					lineHeight: "1.6",
					color: "#5E5E5E",
					margin: 0,
				}}
			>
				{formatBlogDate(blog.created_at)}
				{formatBlogDate(blog.created_at) ? " · " : ""}
				{estimateReadTime(blog.blog_content)}
			</p>

			<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
				<p
					style={{
						fontFamily: "'Geist', sans-serif",
					fontWeight: 500,
					fontSize: "clamp(14px, 1.25vw, 18px)",
					lineHeight: "1.4",
					color: "#132A22",
					margin: 0,
					}}
				>
					{blog.blog_title}
				</p>
				<p
					style={{
						fontFamily: "'Geist', sans-serif",
					fontWeight: 400,
					fontSize: "clamp(12px, 0.97vw, 14px)",
					lineHeight: "1.714",
					color: "#132A22",
					margin: 0,
					}}
				>
					{excerpt}
					{excerpt.length >= 160 && "... "}
					{excerpt.length >= 160 && (
						<span style={{ color: "#3f5445", textDecoration: "underline", fontWeight: 500 }}>
							Read more
						</span>
					)}
				</p>
			</div>
		</div>
	);
};

const BlogPreview = () => {
	const { blogs, fetchBlogs, loading } = useBlogStore();

	useEffect(() => {
		if (blogs.length === 0 && !loading) fetchBlogs(1, 12);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blogs.length, loading]);

	const preview = blogs.slice(0, 3);

	return (
		<>
			<style>{`
				@media (max-width: 767px) {
					.blog-cards-row {
						flex-direction: column !important;
					}
					.blog-skeleton-row {
						flex-direction: column !important;
					}
					.blog-cards-row > div,
					.blog-skeleton-row > div {
						flex: 1 1 auto !important;
					}
					/* Hide the header-row button on mobile */
					.blog-all-articles-desktop {
						display: none !important;
					}
					/* Show the below-cards button on mobile */
					.blog-all-articles-mobile {
						display: inline-flex !important;
					}
				}
			`}</style>

			<section
				style={{
					width: "100%",
					background: "linear-gradient(180deg, #F6F4EE 0%, #FFFFFF 17.18%)",
					boxSizing: "border-box",
					padding: "clamp(48px, 6.94vw, 100px) clamp(20px, 5.56vw, 80px)",
				}}
			>
				<div style={{ maxWidth: "1280px", margin: "0 auto" }}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "clamp(12px, 1.39vw, 20px)",
							marginBottom: "clamp(24px, 2.78vw, 40px)",
						}}
					>
						{/* Label */}
						<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
							<span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#295B4A", flexShrink: 0, display: "inline-block" }} />
							<span
								style={{
									...geistMono,
									fontWeight: 500,
									fontSize: "12px",
									lineHeight: "1",
									letterSpacing: "0.08em",
									color: "#295B4A",
									textTransform: "uppercase",
									whiteSpace: "nowrap",
								}}
							>
								Latest Blogs
							</span>
						</div>

						{/* Heading + All Articles button (same row) */}
						<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
							<h2
								style={{
									...instrumentSerif,
									fontWeight: 400,
									fontSize: "clamp(32px, 4.17vw, 60px)",
									lineHeight: "1.1",
									letterSpacing: "-0.01em",
									color: "#132A22",
									margin: 0,
									maxWidth: "700px",
								}}
							>
								Helpful tips, business insights, and Zikora MFB news.
							</h2>

							<a
								href="/blog"
								className="blog-all-articles-desktop"
								style={{
									fontFamily: "'Geist', sans-serif",
									fontWeight: 500,
									fontSize: "clamp(13px, 1.04vw, 16px)",
									lineHeight: "1",
									color: "#132A22",
									textDecoration: "none",
									border: "1px solid #132A22",
									borderRadius: "8px",
									padding: "10px 20px",
									whiteSpace: "nowrap",
									flexShrink: 0,
									display: "inline-flex",
									alignItems: "center",
									marginTop: "8px",
								}}
							>
								All Articles
							</a>
						</div>
					</div>

					{loading && preview.length === 0 ? (
						<div
							className="blog-skeleton-row"
							style={{
								display: "flex",
								gap: "clamp(10px, 1.11vw, 16px)",
							}}
						>
							{[0, 1, 2].map((i) => (
								<div
									key={i}
									style={{
										flex: 1,
										minWidth: 0,
										height: "clamp(180px, 22.9vw, 330px)",
										backgroundColor: "#f1f1f1",
										borderRadius: "16px",
										animation: "pulse 1.5s ease-in-out infinite",
									}}
								/>
							))}
						</div>
					) : (
						<div
							className="blog-cards-row"
							style={{
								display: "flex",
								gap: "clamp(10px, 1.11vw, 16px)",
								alignItems: "flex-start",
							}}
						>
							{preview.map((blog) => (
								<BlogCard key={blog.id} blog={blog} />
							))}
						</div>
					)}

					{/* All Articles — mobile only, below the cards */}
					<a
						href="/blog"
						className="blog-all-articles-mobile"
						style={{
							display: "none",
							fontFamily: "'Geist', sans-serif",
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: "20px",
							color: "#132A22",
							textDecoration: "none",
							border: "1px solid #132A22",
							borderRadius: "8px",
							padding: "8px 16px",
							whiteSpace: "nowrap",
							alignItems: "center",
							marginTop: "24px",
						}}
					>
						All Articles
					</a>
				</div>
			</section>
		</>
	);
};

export default BlogPreview;
